import { useCallback, useEffect, useState } from 'react';

import type { SubscriptionPaymentSubmission } from '../../components/SubscriptionCheckoutModal';
import { requestJson } from '../../components/workflowShared';
import { runIsDevGitHubAccountEnabled } from './appRunData';

export type AppPlanId = 'none' | 'free' | 'light' | 'professional';
export type AppPaidPlanId = Exclude<AppPlanId, 'none'>;
type AppPlanPeriod = 'week' | 'month';

export interface AppPlan {
  id: AppPlanId;
  name: string;
  priceUsd: number;
  tagline: string;
  scansPerPeriod: number;
  period: AppPlanPeriod;
  workspaceLimit: number;
  messagesPerDay: number;
  features: string[];
  available?: boolean;
}

interface AppPlanCopy {
  id: AppPaidPlanId;
  name: string;
  priceUsd: number;
  tagline: string;
  /** What the plan takes off the team's hands; the allowances are listed around it. */
  outcomes: string[];
}

interface AppPlanLimits {
  scansPerPeriod: number;
  period: AppPlanPeriod;
  workspaceLimit: number;
  messagesPerDay: number;
}

/*
 * Names, prices and outcomes are presented from here so the positioning stays in one place.
 * Allowances still come from the backend when it answers, since that is what it enforces.
 */
const PLAN_COPY: AppPlanCopy[] = [
  {
    id: 'free',
    name: 'Security Scan',
    priceUsd: 55,
    tagline: 'Find out where an application stands today.',
    outcomes: [
      'Prioritized findings with fix guidance',
      'Assessment report to share'
    ]
  },
  {
    id: 'light',
    name: 'Security Lead',
    priceUsd: 220,
    tagline: 'Your AI security partner for what insurers, investors and customers ask.',
    outcomes: [
      'Drafted answers to security questionnaires',
      'Quote requests for third-party audits and pentests, scoped from your code',
      'Re-assessment when Havu\'s AI analysis is updated'
    ]
  },
  {
    id: 'professional',
    name: 'Security Officer',
    priceUsd: 499,
    tagline: 'For companies answering to auditors and due diligence across many applications.',
    outcomes: [
      'Security summary for insurers, investors and auditors'
    ]
  }
];

const DEFAULT_LIMITS: Record<AppPaidPlanId, AppPlanLimits> = {
  free: { scansPerPeriod: 5, period: 'month', workspaceLimit: 5, messagesPerDay: 3 },
  light: { scansPerPeriod: 30, period: 'month', workspaceLimit: 20, messagesPerDay: 50 },
  professional: { scansPerPeriod: 90, period: 'month', workspaceLimit: 100, messagesPerDay: 200 }
};

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

const toAppPlan = (copy: AppPlanCopy, limits: AppPlanLimits, available?: boolean): AppPlan => ({
  id: copy.id,
  name: copy.name,
  priceUsd: copy.priceUsd,
  tagline: copy.tagline,
  ...limits,
  features: [
    copy.id === 'free'
      ? `Up to ${plural(limits.workspaceLimit, 'application')}`
      : `Up to ${limits.workspaceLimit} managed applications`,
    `${plural(limits.scansPerPeriod, 'security assessment')} per ${limits.period}`,
    ...copy.outcomes,
    `${plural(limits.messagesPerDay, 'AppSec question')} per day`
  ],
  ...(available === undefined ? {} : { available })
});

export const APP_PLANS: AppPlan[] = PLAN_COPY.map((copy) => toAppPlan(copy, DEFAULT_LIMITS[copy.id]));

export const NO_SUBSCRIPTION_PLAN: AppPlan = {
  id: 'none',
  name: 'No subscription',
  priceUsd: 0,
  tagline: 'Chat access without security assessments.',
  scansPerPeriod: 0,
  period: 'month',
  workspaceLimit: 1,
  messagesPerDay: 3,
  features: ['1 workspace', '3 AppSec questions per day']
};

/** Whether Havu handles outside requests: questionnaires, quotes and re-assessment on analysis updates. */
export const planHasPartner = (id: AppPlanId): boolean => id === 'light' || id === 'professional';

/** Whether Havu can produce a security summary for outside parties. */
export const planHasSecuritySummary = (id: AppPlanId): boolean => id === 'professional';

interface AppSubscriptionState {
  plans?: Array<{
    id: AppPaidPlanId;
    name: string;
    priceUsd: number;
    tagline: string;
    scansPerPeriod: number;
    scanPeriodDays: number;
    messagesPerPeriod: number;
    messagePeriodDays: number;
    workspaceLimit: number;
    features: string[];
    available: boolean;
  }>;
  subscription?: {
    planId: AppPaidPlanId;
    status: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd: boolean;
  };
  entitlement: {
    planId: AppPlanId;
    scansUsed: number;
    scansLimit: number;
    scansLeft: number;
    resetsAt?: string;
    messagesUsed: number;
    messagesLimit: number;
    messagesLeft: number;
    messagesResetAt?: string;
    workspaceLimit: number;
  };
}

interface SubscriptionCheckoutResponse {
  status: 'active' | 'requires-action';
  clientSecret?: string;
  state?: AppSubscriptionState;
}

export const getAppPlan = (id: AppPlanId): AppPlan => id === 'none'
  ? NO_SUBSCRIPTION_PLAN
  : APP_PLANS.find((plan) => plan.id === id) ?? NO_SUBSCRIPTION_PLAN;

export const getAppPlanRank = (id: AppPlanId): number => APP_PLANS.findIndex((plan) => plan.id === id);

export const formatResetsIn = (resetsAt: number): string => {
  const hourMs = 60 * 60 * 1000;
  const dayMs = 24 * hourMs;
  const remainingMs = Math.max(resetsAt - Date.now(), 0);

  if (remainingMs < hourMs) return 'in less than an hour';
  if (remainingMs < dayMs) {
    const hours = Math.ceil(remainingMs / hourMs);
    return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  const days = Math.ceil(remainingMs / dayMs);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
};

export const checkoutSubscription = async (
  planId: AppPaidPlanId,
  { paymentMethodId, requestId, stripe }: SubscriptionPaymentSubmission
): Promise<AppSubscriptionState> => {
  let response = await requestJson<SubscriptionCheckoutResponse>('/subscription/checkout', {
    method: 'POST',
    body: JSON.stringify({ planId, paymentMethodId, requestId })
  });

  if (response.status === 'requires-action') {
    if (!response.clientSecret) {
      throw new Error('Stripe authentication is required, but the subscription response was incomplete.');
    }

    const confirmation = await stripe.confirmCardPayment(response.clientSecret);

    if (confirmation.error) throw new Error(confirmation.error.message || 'Stripe authentication failed.');
    if (confirmation.paymentIntent?.status !== 'succeeded') {
      throw new Error('The subscription payment has not completed.');
    }

    response = await requestJson<SubscriptionCheckoutResponse>('/subscription/confirm', { method: 'POST' });
  }

  if (response.status !== 'active' || !response.state) {
    throw new Error('The subscription is not active yet.');
  }

  return response.state;
};

export const cancelCurrentSubscription = async (): Promise<AppSubscriptionState> => {
  return requestJson<AppSubscriptionState>('/subscription', { method: 'DELETE' });
};

const defaultState = (): AppSubscriptionState => ({
  entitlement: {
    planId: 'none',
    scansUsed: 0,
    scansLimit: 0,
    scansLeft: 0,
    messagesUsed: 0,
    messagesLimit: 3,
    messagesLeft: 3,
    workspaceLimit: 1
  }
});

const devState = (): AppSubscriptionState => {
  const now = new Date();
  const currentPeriodStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const currentPeriodEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));

  return {
    subscription: {
      planId: 'professional',
      status: 'active',
      currentPeriodStart: currentPeriodStart.toISOString(),
      currentPeriodEnd: currentPeriodEnd.toISOString(),
      cancelAtPeriodEnd: false
    },
    entitlement: {
      planId: 'professional',
      // The three seeded dev scans each use one default-workflow equivalent.
      scansUsed: 3,
      scansLimit: 90,
      scansLeft: 87,
      resetsAt: currentPeriodEnd.toISOString(),
      messagesUsed: 0,
      messagesLimit: 200,
      messagesLeft: 200,
      workspaceLimit: 100
    }
  };
};

/** Subscription state comes from the backend except for the explicitly enabled local dev account. */
export function useAppPlan(isSignedIn: boolean) {
  const isDev = runIsDevGitHubAccountEnabled();
  const [state, setState] = useState<AppSubscriptionState>(() => isDev ? devState() : defaultState());
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (isDev) {
      setState(devState());
      return;
    }

    if (!isSignedIn) {
      const response = await requestJson<{ plans: NonNullable<AppSubscriptionState['plans']> }>('/subscriptions/plans');
      setState({ ...defaultState(), plans: response.plans });
      return;
    }

    setIsLoading(true);
    try {
      setState(await requestJson<AppSubscriptionState>('/subscription'));
    } finally {
      setIsLoading(false);
    }
  }, [isDev, isSignedIn]);

  useEffect(() => {
    void refresh().catch((error: unknown) => console.error('Failed to load subscription:', error));
  }, [refresh]);

  const plans = state.plans ? PLAN_COPY.map((copy): AppPlan => {
    const served = state.plans?.find((entry) => entry.id === copy.id);

    return served
      ? toAppPlan(copy, {
        scansPerPeriod: served.scansPerPeriod,
        period: served.scanPeriodDays >= 28 ? 'month' : 'week',
        workspaceLimit: served.workspaceLimit,
        // Message allowances reset daily today; scale any other window to a day.
        messagesPerDay: Math.max(1, Math.floor(served.messagesPerPeriod / Math.max(1, served.messagePeriodDays)))
      }, served.available)
      : toAppPlan(copy, DEFAULT_LIMITS[copy.id], false);
  }) : APP_PLANS;
  const plan = state.entitlement.planId === 'none'
    ? NO_SUBSCRIPTION_PLAN
    : plans.find((entry) => entry.id === state.entitlement.planId) ?? getAppPlan(state.entitlement.planId);

  return {
    plans,
    plan,
    scansLeft: state.entitlement.scansLeft,
    resetsAt: state.entitlement.resetsAt ? new Date(state.entitlement.resetsAt).getTime() : null,
    subscription: state.subscription,
    isLoading,
    applyState: setState,
    refresh
  };
}
